import { lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import type { Permission } from '@shawwir/contracts';
import { AppShell } from '@/components/layout/AppShell';
import { RouteErrorBoundary, NotFoundPage } from '@/components/layout/ErrorBoundary';
import { LoginPage, RequireAuth } from '@/features/auth/LoginPage';
import { AuthProvider, useAuth } from '@/lib/auth';
import { ToastProvider } from '@/components/ui/toast';

/**
 * NFR-5: every page is code-split, so the initial bundle carries the shell and
 * little else -- against the legacy app's multi-megabyte jQuery vendor payload.
 */
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const RequestsPage = lazy(() =>
  import('@/features/requests/RequestsPage').then((m) => ({ default: m.RequestsPage })),
);
const RequestForm = lazy(() =>
  import('@/features/requests/RequestForm').then((m) => ({ default: m.RequestForm })),
);
const AppointmentsPage = lazy(() =>
  import('@/features/appointments/AppointmentsPage').then((m) => ({ default: m.AppointmentsPage })),
);
const OrdersPage = lazy(() =>
  import('@/features/orders/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const OrderDetail = lazy(() =>
  import('@/features/orders/OrderDetail').then((m) => ({ default: m.OrderDetail })),
);
const LawyersPage = lazy(() =>
  import('@/features/lawyers/LawyersPage').then((m) => ({ default: m.LawyersPage })),
);
const LawyerForm = lazy(() =>
  import('@/features/lawyers/LawyerForm').then((m) => ({ default: m.LawyerForm })),
);
const LawyerDetail = lazy(() =>
  import('@/features/lawyers/LawyerDetail').then((m) => ({ default: m.LawyerDetail })),
);
const LawyerQueuePage = lazy(() =>
  import('@/features/lawyers/verification/LawyerQueuePage').then((m) => ({
    default: m.LawyerQueuePage,
  })),
);
const LawyerReviewPage = lazy(() =>
  import('@/features/lawyers/verification/LawyerReviewPage').then((m) => ({
    default: m.LawyerReviewPage,
  })),
);
const ClientsPage = lazy(() =>
  import('@/features/clients/ClientsPage').then((m) => ({ default: m.ClientsPage })),
);
const ClientForm = lazy(() =>
  import('@/features/clients/ClientsPage').then((m) => ({ default: m.ClientForm })),
);
const ServicesPage = lazy(() =>
  import('@/features/services/ServicesPage').then((m) => ({ default: m.ServicesPage })),
);
const ServiceForm = lazy(() =>
  import('@/features/services/ServiceForm').then((m) => ({ default: m.ServiceForm })),
);
const EmergenciesPage = lazy(() =>
  import('@/features/emergencies/EmergenciesPage').then((m) => ({ default: m.EmergenciesPage })),
);
const DependenciesPage = lazy(() =>
  import('@/features/dependencies/DependenciesPage').then((m) => ({ default: m.DependenciesPage })),
);
const PromocodesPage = lazy(() =>
  import('@/features/promocodes/PromocodesPage').then((m) => ({ default: m.PromocodesPage })),
);
const PromocodeForm = lazy(() =>
  import('@/features/promocodes/PromocodeForm').then((m) => ({ default: m.PromocodeForm })),
);
const ConsultationsPage = lazy(() =>
  import('@/features/consultations/ConsultationsPage').then((m) => ({
    default: m.ConsultationsPage,
  })),
);
const ConsultationDetail = lazy(() =>
  import('@/features/consultations/ConsultationDetail').then((m) => ({
    default: m.ConsultationDetail,
  })),
);
const ConsultationSettingsPage = lazy(() =>
  import('@/features/consultations/ConsultationSettingsPage').then((m) => ({
    default: m.ConsultationSettingsPage,
  })),
);
const BalancesPage = lazy(() =>
  import('@/features/balances/BalancesPage').then((m) => ({ default: m.BalancesPage })),
);
const BalanceDetail = lazy(() =>
  import('@/features/balances/BalanceDetail').then((m) => ({ default: m.BalanceDetail })),
);
const AuditPage = lazy(() =>
  import('@/features/settings/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const UsersPage = lazy(() =>
  import('@/features/settings/UsersPage').then((m) => ({ default: m.UsersPage })),
);

/**
 * AUTH-9: hides a route the user cannot use. This is convenience only -- the
 * BFF enforces the same permission server-side, so removing this guard in the
 * browser would change nothing about what the API allows.
 */
function RequirePermission({ permission }: { permission: Permission }) {
  const { can, status } = useAuth();
  if (status !== 'authenticated') return null;
  return can(permission) ? <Outlet /> : <Navigate to="/" replace />;
}

/**
 * The providers live INSIDE the router, because the auth context navigates on
 * a 401 (AUTH-6) and therefore needs router context of its own.
 */
function Providers() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </ToastProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <Providers />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: '/',
        // AUTH-3: everything below this sits behind the guard.
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
      { index: true, element: <DashboardPage />, errorElement: <RouteErrorBoundary /> },

      {
        element: <RequirePermission permission="requests:read" />,
        children: [
          { path: 'requests', element: <RequestsPage />, errorElement: <RouteErrorBoundary /> },
          { path: 'requests/new', element: <RequestForm />, errorElement: <RouteErrorBoundary /> },
          { path: 'requests/:id/edit', element: <RequestForm />, errorElement: <RouteErrorBoundary /> },
          // The detail route reuses the form until a richer timeline exists.
          { path: 'requests/:id', element: <RequestForm />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        element: <RequirePermission permission="appointments:read" />,
        children: [
          { path: 'appointments', element: <AppointmentsPage />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        element: <RequirePermission permission="orders:read" />,
        children: [
          { path: 'orders', element: <OrdersPage />, errorElement: <RouteErrorBoundary /> },
          { path: 'orders/:id', element: <OrderDetail />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        /**
         * LAW-11. Declared before the lawyers:read block so the static
         * `lawyers/verification` segment is unambiguous to a reader (React
         * Router ranks it above `lawyers/:id` regardless, but the ordering
         * makes the intent obvious). Guarded by lawyers:verify, which a
         * customer-service agent has and which lawyers:write is not.
         */
        element: <RequirePermission permission="lawyers:verify" />,
        children: [
          {
            path: 'lawyers/verification',
            element: <LawyerQueuePage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'lawyers/:id/verification',
            element: <LawyerReviewPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },

      {
        element: <RequirePermission permission="lawyers:read" />,
        children: [
          { path: 'lawyers', element: <LawyersPage />, errorElement: <RouteErrorBoundary /> },
          { path: 'lawyers/new', element: <LawyerForm />, errorElement: <RouteErrorBoundary /> },
          { path: 'lawyers/:id', element: <LawyerDetail />, errorElement: <RouteErrorBoundary /> },
          { path: 'lawyers/:id/edit', element: <LawyerForm />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        element: <RequirePermission permission="clients:read" />,
        children: [
          { path: 'clients', element: <ClientsPage />, errorElement: <RouteErrorBoundary /> },
          { path: 'clients/new', element: <ClientForm />, errorElement: <RouteErrorBoundary /> },
          { path: 'clients/:id/edit', element: <ClientForm />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        element: <RequirePermission permission="services:read" />,
        children: [
          { path: 'services', element: <ServicesPage />, errorElement: <RouteErrorBoundary /> },
          { path: 'services/new', element: <ServiceForm />, errorElement: <RouteErrorBoundary /> },
          { path: 'services/:id/edit', element: <ServiceForm />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        element: <RequirePermission permission="emergencies:read" />,
        children: [
          { path: 'emergencies', element: <EmergenciesPage />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        element: <RequirePermission permission="dependencies:read" />,
        children: [
          { path: 'dependencies', element: <DependenciesPage />, errorElement: <RouteErrorBoundary /> },
        ],
      },

      {
        element: <RequirePermission permission="promocodes:read" />,
        children: [
          { path: 'promocodes', element: <PromocodesPage />, errorElement: <RouteErrorBoundary /> },
          { path: 'promocodes/new', element: <PromocodeForm />, errorElement: <RouteErrorBoundary /> },
          {
            path: 'promocodes/:id/edit',
            element: <PromocodeForm />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },

      {
        element: <RequirePermission permission="consultations:read" />,
        children: [
          {
            path: 'consultations',
            element: <ConsultationsPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'consultations/settings',
            element: <ConsultationSettingsPage />,
            errorElement: <RouteErrorBoundary />,
          },
          // After the static segment above; the router ranks it below either
          // way, but the ordering says so to the reader too.
          {
            path: 'consultations/:id',
            element: <ConsultationDetail />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },

      {
        element: <RequirePermission permission="balances:read" />,
        children: [
          { path: 'balances', element: <BalancesPage />, errorElement: <RouteErrorBoundary /> },
          {
            path: 'balances/:lawyerId',
            element: <BalanceDetail />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },

      {
        element: <RequirePermission permission="audit:read" />,
        children: [{ path: 'audit', element: <AuditPage />, errorElement: <RouteErrorBoundary /> }],
      },

      {
        element: <RequirePermission permission="users:manage" />,
        children: [
          { path: 'settings/users', element: <UsersPage />, errorElement: <RouteErrorBoundary /> },
        ],
      },

          // SHL-7: a 404 that links somewhere real.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
